import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  RiInformationLine, 
  RiEyeLine, 
  RiShieldCheckFill,
  RiLockFill,
  RiPhoneFill,
  RiCheckboxCircleFill,
  RiArrowRightLine,
  RiMessageLine,
  RiSendPlaneLine,
  RiCheckboxCircleLine,
  RiArrowDownSLine,
  RiQuestionAnswerLine,
  RiShieldCrossLine,
  RiCustomerService2Line,
  RiGlobalLine,
  RiSearchLine,
  RiFileAddLine,
  RiQuestionLine
} from 'react-icons/ri';
import SEO from '../components/SEO';
import './Home.css';

const Home = () => {
  const [activeTab, setActiveTab] = useState('imei-check');
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (category, index) => {
    const key = `${category}-${index}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const faqData = {
    'imei-check': [
      {
        category: 'IMEI Basics',
        questions: [
          {
            q: 'What is IMEI number?',
            a: 'IMEI (International Mobile Equipment Identity) is a unique 15-digit number that identifies your mobile device. It is used to verify the authenticity and registration status of your phone in the NEIR database.'
          },
          {
            q: 'How to find my IMEI number?',
            a: 'You can find your IMEI number by dialing *#06# on your phone, or by checking the device settings, or by looking at the original packaging or receipt of your device.'
          }
        ]
      },
      {
        category: 'IMEI Status Meanings',
        questions: [
          {
            q: 'What does "Verified" mean in IMEI Check?',
            a: '"Verified" means your device is registered in the NEIR database and is authentic. It has been legally imported and is not reported as lost, stolen, or cloned.'
          },
          {
            q: 'What does "Stolen or Blocked" mean?',
            a: 'This status indicates that the device has been reported as lost or stolen, or has been blocked by the authorities. Such devices cannot be used on any network in Bangladesh.'
          }
        ]
      },
      {
        category: 'Troubleshooting Special Cases',
        questions: [
          {
            q: 'Why am I seeing multiple IMEI numbers on my dashboard?',
            a: 'Dual SIM phones have two IMEI numbers - one for each SIM slot. Both IMEI numbers need to be checked separately to verify the registration status of your device.'
          },
          {
            q: 'Why does my new phone show "Not Registered"?',
            a: 'New devices may take some time to be registered in the database. If your device is legally imported, it should be registered automatically. If the issue persists, contact your service provider or BTRC.'
          },
          {
            q: 'What if IMEI Check shows the wrong model or device details?',
            a: 'If the device details are incorrect, it may indicate a cloned or counterfeit device. Please verify your device with the original manufacturer and contact BTRC for assistance.'
          },
          {
            q: 'My phone has more than one SIM. Which one should I check?',
            a: 'For dual SIM phones, you should check both IMEI numbers. Each SIM slot has its own IMEI number, and both need to be verified separately.'
          }
        ]
      }
    ],
    'registration': [
      {
        category: 'Understanding Special Registration',
        questions: [
          {
            q: 'What is special registration?',
            a: 'Special Registration is a process for registering mobile devices that were not registered during import. This is typically required for devices brought by foreign visitors or devices that need to be registered after purchase.'
          },
          {
            q: 'I am a foreign visitor. Do I need to register my phone?',
            a: 'Yes, foreign visitors need to register their mobile devices through the Special Registration process if they plan to use their device in Bangladesh for an extended period.'
          }
        ]
      },
      {
        category: 'Usage Duration',
        questions: [
          {
            q: 'How long can I use my phone without special registration?',
            a: 'Unregistered devices can typically be used for a limited period (usually 30-90 days) before they are blocked from the network. It is recommended to complete registration as soon as possible.'
          },
          {
            q: 'How long does Special Registration take?',
            a: 'Special Registration processing time varies, but typically takes 3-7 business days after submission of all required documents and payment of fees.'
          }
        ]
      },
      {
        category: 'Requirements and Rules',
        questions: [
          {
            q: 'What documents are required?',
            a: 'Required documents typically include: valid passport or national ID, proof of purchase or invoice, device IMEI number, and completed registration form. Additional documents may be required for foreign visitors.'
          },
          {
            q: 'Can Special Registration be rejected?',
            a: 'Yes, Special Registration can be rejected if the device is reported as stolen, cloned, or if required documents are incomplete or invalid. You will be notified of the reason for rejection.'
          }
        ]
      },
      {
        category: 'De-registration',
        questions: [
          {
            q: 'When to use De-registration?',
            a: 'De-registration is used when you want to remove your device from the NEIR database, such as when selling the device, permanently leaving the country, or when the device is no longer in use.'
          },
          {
            q: 'How can I De-register my device?',
            a: 'You can de-register your device through the NEIR Citizen Portal by submitting a de-registration request with your device IMEI number and required verification documents.'
          }
        ]
      }
    ],
    'lost-block': [
      {
        category: 'Reporting a Lost or Stolen Device',
        questions: [
          {
            q: 'How can I report my device as stolen?',
            a: 'You can report your device as stolen through the NEIR Citizen Portal by logging into your account, selecting "Report Lost/Stolen Device", and providing your device IMEI number and relevant details.'
          },
          {
            q: 'What do I need to submit a lost or stolen report?',
            a: 'You need your device IMEI number, police report (if applicable), proof of ownership, and your account credentials. The report should be submitted as soon as possible after the incident.'
          }
        ]
      },
      {
        category: 'After the device is stolen',
        questions: [
          {
            q: 'What happens if someone inserts a new SIM in my stolen phone?',
            a: 'Once reported as stolen, the device will be blocked from all networks. If someone tries to use a new SIM card, the device will be automatically blocked and cannot be used.'
          },
          {
            q: 'Why should I report a stolen phone on NEIR?',
            a: 'Reporting your stolen phone helps prevent unauthorized use, protects your personal data, assists law enforcement in recovery efforts, and helps maintain the security of the mobile network.'
          }
        ]
      },
      {
        category: 'IMEI Related Issues',
        questions: [
          {
            q: 'What if I don\'t know the IMEI of my stolen phone?',
            a: 'You can find your IMEI number from your device purchase receipt, original packaging, device settings backup, or by checking your NEIR account if the device was previously registered.'
          }
        ]
      },
      {
        category: 'Device Recovery',
        questions: [
          {
            q: 'How can I report my device as found?',
            a: 'If you recover your device, you can report it as found through the NEIR Citizen Portal. This will unblock the device and restore its network access after verification.'
          }
        ]
      }
    ],
    'account-help': [
      {
        category: 'Account Management',
        questions: [
          {
            q: 'How do I create a NEIR account?',
            a: 'You can create a NEIR account by visiting the NEIR Citizen Portal, clicking on "Register" or "Sign Up", and providing your personal information, email address, and mobile number for verification.'
          },
          {
            q: 'I forgot my password. How can I reset it?',
            a: 'You can reset your password by clicking "Forgot Password" on the login page, entering your registered email or mobile number, and following the instructions sent to you.'
          },
          {
            q: 'How can I update my account information?',
            a: 'You can update your account information by logging into your NEIR account, going to "Profile Settings" or "Account Settings", and making the necessary changes.'
          }
        ]
      },
      {
        category: 'Technical Support',
        questions: [
          {
            q: 'I\'m having trouble accessing the portal. What should I do?',
            a: 'Try clearing your browser cache, using a different browser, or checking your internet connection. If the problem persists, contact NEIR support for assistance.'
          },
          {
            q: 'How can I contact NEIR support?',
            a: 'You can contact NEIR support through the Contact page on the website, by email, or by calling the BTRC helpline. Support is available during business hours.'
          }
        ]
      }
    ],
    'about-neir': [
      {
        category: 'About NEIR',
        questions: [
          {
            q: 'What is NEIR?',
            a: 'NEIR (National Equipment Identity Register) is a centralized database system managed by BTRC that registers and tracks all mobile devices in Bangladesh to prevent the use of stolen, cloned, or illegally imported devices.'
          },
          {
            q: 'Why is NEIR important?',
            a: 'NEIR is important for preventing criminal activities, ensuring device authenticity, protecting consumers from stolen or counterfeit devices, and maintaining the security and integrity of the mobile network in Bangladesh.'
          }
        ]
      },
      {
        category: 'Users & Access',
        questions: [
          {
            q: 'Who should use the NEIR Citizen Portal?',
            a: 'All mobile device users in Bangladesh should use the NEIR Citizen Portal to verify their device registration status, register new devices, report lost or stolen devices, and manage their device information.'
          }
        ]
      },
      {
        category: 'Device Coverage & Control',
        questions: [
          {
            q: 'Does NEIR block illegal or stolen devices?',
            a: 'Yes, NEIR automatically blocks devices that are reported as lost or stolen, cloned devices, or devices that are not properly registered in the database. Blocked devices cannot be used on any network.'
          },
          {
            q: 'What devices does NEIR cover?',
            a: 'NEIR covers all mobile phones, smartphones, tablets, and other mobile devices that use SIM cards and connect to cellular networks in Bangladesh.'
          }
        ]
      },
      {
        category: 'Privacy & Security',
        questions: [
          {
            q: 'Is my personal information safe?',
            a: 'Yes, NEIR follows strict data protection and privacy policies. Your personal information is encrypted and securely stored. Access to your data is restricted to authorized personnel only, and your information is not shared with third parties without your consent.'
          }
        ]
      }
    ]
  };

  return (
    <>
      <SEO
        title="NEIR BTRC - National Equipment Identity Register | Official Bangladesh Government Portal"
        description="Verify your phone's IMEI number with the official Bangladesh Mobile Device Database. National Equipment Identity Register (NEIR) by Bangladesh Telecommunication Regulatory Commission (BTRC). Check if your device is authentic, imported legally, and not reported as lost, stolen, or cloned. Official government portal for mobile device registration and IMEI verification in Bangladesh."
        keywords="NEIR, NEIR BTRC, NEIR Bangladesh, NEIR.gov.bd, NEIR.btrc.gov.bd, neir.btrc.gov.bd, BTRC, BTRC.gov.bd, btrc.gov.bd, National Equipment Identity Register, Bangladesh Telecommunication Regulatory Commission, IMEI verification, IMEI check, IMEI number check, IMEI verification Bangladesh, IMEI check Bangladesh, mobile device registration, Bangladesh government portal, official mobile database, device authentication, phone verification, government of Bangladesh, BTRC NEIR, mobile device database, Bangladesh mobile registration, official IMEI verification, government mobile database, BTRC portal, NEIR portal, mobile device verification Bangladesh, government telecommunications, Bangladesh mobile network, device identity register, official government website, Bangladesh telecom regulatory, mobile device security, IMEI database Bangladesh, government mobile services, BTRC official website, NEIR official website, Bangladesh mobile device verification, government mobile registration, official device check, mobile authentication Bangladesh, government telecommunications commission, Bangladesh mobile network security, device registration portal, official mobile verification, Bangladesh government services, mobile device compliance, government mobile database system, neir.btrc.gov.bd, www.neir.btrc.gov.bd, neir portal, btrc neir portal, neir btrc website, neir btrc official, neir btrc login, neir btrc registration, neir btrc check, neir btrc verify, neir btrc database, neir btrc system, neir btrc online, neir btrc service, neir btrc mobile, neir btrc device, neir btrc imei, neir btrc bangladesh, neir btrc gov, neir btrc government, neir btrc official website, neir btrc official portal, neir btrc official site, neir btrc official service, neir btrc official database, neir btrc official system, neir btrc official check, neir btrc official verify, neir btrc official registration, neir btrc official login, neir btrc official mobile, neir btrc official device, neir btrc official imei, neir btrc official bangladesh, neir btrc official gov, neir btrc official government, .gov.bd, gov.bd domain, government domain Bangladesh, official government domain, Bangladesh government website, government portal Bangladesh, official portal Bangladesh, government service Bangladesh, official service Bangladesh, government database Bangladesh, official database Bangladesh, government system Bangladesh, official system Bangladesh, government website Bangladesh, official website Bangladesh, government portal .gov.bd, official portal .gov.bd, government service .gov.bd, official service .gov.bd, government database .gov.bd, official database .gov.bd, government system .gov.bd, official system .gov.bd, government website .gov.bd, official website .gov.bd, btrc.gov.bd, neir.btrc.gov.bd, www.btrc.gov.bd, www.neir.btrc.gov.bd, btrc portal, neir portal, btrc website, neir website, btrc official, neir official, btrc login, neir login, btrc registration, neir registration, btrc check, neir check, btrc verify, neir verify, btrc database, neir database, btrc system, neir system, btrc online, neir online, btrc service, neir service, btrc mobile, neir mobile, btrc device, neir device, btrc imei, neir imei, btrc bangladesh, neir bangladesh, btrc gov, neir gov, btrc government, neir government"
        ogTitle="NEIR BTRC - National Equipment Identity Register | Official Bangladesh Government"
        ogDescription="Verify your phone's IMEI number with the official Bangladesh Mobile Device Database. Official government portal by BTRC."
        ogImage="/images/btrc-logo.png"
        canonicalUrl="https://neir.btrc.gov.bd/"
      />
      <div className="home">
        {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="container">
          <div className="hero-content">
            <div className="hero-text animate-fadeInLeft">
              <h1 className="hero-title">
                <span className="gradient-text">National Equipment Identity Register</span>
              </h1>
              <p className="hero-subtitle">
                Verify your phone's IMEI number with the official Bangladesh Mobile Device Database. 
                Find out if your device is authentic, imported legally, and not reported as lost, stolen, or cloned.
              </p>
              <div className="hero-buttons">
                <a 
                  href="https://btrc.portal.gov.bd/site/page/19ac6abd-d10b-4cd3-80ba-0c83ebed7c1b" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn btn-primary btn-lg"
                >
                  <RiInformationLine /> Learn More
                </a>
                <a 
                  href="https://neir.btrc.gov.bd/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn btn-outline btn-lg"
                >
                  <RiEyeLine /> Check IMEI
                </a>
              </div>
            </div>
            <div className="hero-image animate-fadeInRight">
              <div className="hero-banner">
                <img 
                  src="/gifs/neir-herobanner.gif" 
                  alt="NEIR Banner" 
                  className="banner-img"
                />
              </div>
              <div className="floating-cards">
                <div className="floating-card card-1 animate-float">
                  <RiShieldCheckFill />
                  <span>Verified Device</span>
                </div>
                <div className="floating-card card-2 animate-float" style={{ animationDelay: '1s' }}>
                  <RiLockFill />
                  <span>Secure Database</span>
                </div>
                <div className="floating-card card-3 animate-float" style={{ animationDelay: '2s' }}>
                  <RiPhoneFill />
                  <span>IMEI Check</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="wave-divider">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* IMEI Verification Steps Section */}
      <section className="verification-steps-section section">
        <div className="container">
          <div className="section-header text-center">
            <h2 className="section-title">
              <span className="gradient-text">3 Steps to Check Phone Registration Status by IMEI</span>
            </h2>
            <p className="section-subtitle">
              Follow these simple steps to verify your mobile device registration
            </p>
          </div>
          <div className="steps-container">
            <div className="step-card step-1">
              <div className="step-number-badge">1</div>
              <div className="step-illustration">
                <img 
                  src="/images/step-1.svg" 
                  alt="Step 1 - Type KYD and IMEI" 
                  className="step-image"
                />
              </div>
              <h3 className="step-title">Step 1</h3>
              <p className="step-description">
                Go to your mobile phone's messaging option and type <strong>KYD</strong>, add a space, 
                then enter the 15-digit IMEI number.
              </p>
              <div className="step-example">
                <RiMessageLine className="example-icon" />
                <code>KYD 123456789012345</code>
              </div>
            </div>

            <div className="step-arrow">
              <RiArrowRightLine />
            </div>

            <div className="step-card step-2">
              <div className="step-number-badge">2</div>
              <div className="step-illustration">
                <img 
                  src="/images/step-2.svg" 
                  alt="Step 2 - Send to 16002" 
                  className="step-image"
                />
              </div>
              <h3 className="step-title">Step 2</h3>
              <p className="step-description">
                Send the message to <strong>16002</strong>
              </p>
              <div className="step-example">
                <RiSendPlaneLine className="example-icon" />
                <span>Send to 16002</span>
              </div>
            </div>

            <div className="step-arrow">
              <RiArrowRightLine />
            </div>

            <div className="step-card step-3">
              <div className="step-number-badge">3</div>
              <div className="step-illustration">
                <img 
                  src="/images/step-3.svg" 
                  alt="Step 3 - Verification Result" 
                  className="step-image"
                />
              </div>
              <h3 className="step-title">Step 3</h3>
              <p className="step-description">
                You will instantly receive a return message with the validity status of your mobile handset.
              </p>
              <div className="step-example">
                <RiCheckboxCircleLine className="example-icon success" />
                <span>Instant Verification</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section section">
        <div className="container">
          <div className="section-header text-center">
            <h2 className="section-title">
              <span className="gradient-text">Key Benefits</span>
            </h2>
            <p className="section-subtitle">
              Discover how NEIR protects and enhances mobile device security in Bangladesh
            </p>
          </div>
          <div className="benefits-grid">
            <div className="benefit-card benefit-card-1">
              <div className="benefit-card-icon">
                <RiShieldCheckFill />
              </div>
              <div className="benefit-card-content">
                <h3 className="benefit-card-title">Prevent Criminal Activities</h3>
                <p className="benefit-card-description">
                  Stop the use of stolen or counterfeit mobile devices, extortion, and harassment through comprehensive device verification.
                </p>
              </div>
              <div className="benefit-card-decoration"></div>
            </div>

            <div className="benefit-card benefit-card-2">
              <div className="benefit-card-icon">
                <RiCheckboxCircleFill />
              </div>
              <div className="benefit-card-content">
                <h3 className="benefit-card-title">Better Citizen Services</h3>
                <p className="benefit-card-description">
                  Provide analytical information and better services through the centralized system for improved user experience.
                </p>
              </div>
              <div className="benefit-card-decoration"></div>
            </div>

            <div className="benefit-card benefit-card-3">
              <div className="benefit-card-icon">
                <RiLockFill />
              </div>
              <div className="benefit-card-content">
                <h3 className="benefit-card-title">User Authentication</h3>
                <p className="benefit-card-description">
                  Ensure proper user authentication and device accountability across all networks for enhanced security.
                </p>
              </div>
              <div className="benefit-card-decoration"></div>
            </div>

            <div className="benefit-card benefit-card-4">
              <div className="benefit-card-icon">
                <RiPhoneFill />
              </div>
              <div className="benefit-card-content">
                <h3 className="benefit-card-title">Network Monitoring</h3>
                <p className="benefit-card-description">
                  Monitor Cellular Mobile Phone Operators (CMPO) EIRs for compliance and security standards.
                </p>
              </div>
              <div className="benefit-card-decoration"></div>
            </div>
          </div>
          <div className="benefits-cta text-center">
            <Link to="/about" className="btn btn-primary btn-lg">
              <RiArrowRightLine /> Learn More About NEIR
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section section">
        <div className="container">
          <div className="faq-header">
            <div className="faq-title-wrapper">
              <div className="faq-title-icon">
                <RiQuestionAnswerLine />
              </div>
              <h2 className="faq-title">FAQ</h2>
            </div>
            <p className="faq-subtitle">Need help?</p>
          </div>

          <div className="faq-tabs">
            <button 
              className={`faq-tab ${activeTab === 'imei-check' ? 'active' : ''}`}
              onClick={() => setActiveTab('imei-check')}
            >
              IMEI Check
            </button>
            <button 
              className={`faq-tab ${activeTab === 'registration' ? 'active' : ''}`}
              onClick={() => setActiveTab('registration')}
            >
              Registration
            </button>
            <button 
              className={`faq-tab ${activeTab === 'lost-block' ? 'active' : ''}`}
              onClick={() => setActiveTab('lost-block')}
            >
              Lost & Block
            </button>
            <button 
              className={`faq-tab ${activeTab === 'account-help' ? 'active' : ''}`}
              onClick={() => setActiveTab('account-help')}
            >
              Account & Help
            </button>
            <button 
              className={`faq-tab ${activeTab === 'about-neir' ? 'active' : ''}`}
              onClick={() => setActiveTab('about-neir')}
            >
              About NEIR
            </button>
          </div>

          <div className="faq-content">
            {faqData[activeTab].map((section, sectionIndex) => (
              <div key={sectionIndex} className="faq-category">
                <div className="faq-category-header">
                  <div className="faq-category-icon-wrapper">
                    {activeTab === 'imei-check' && <RiSearchLine />}
                    {activeTab === 'registration' && <RiFileAddLine />}
                    {activeTab === 'lost-block' && <RiShieldCrossLine />}
                    {activeTab === 'account-help' && <RiCustomerService2Line />}
                    {activeTab === 'about-neir' && <RiGlobalLine />}
                  </div>
                  <h3 className="faq-category-title">{section.category}</h3>
                </div>
                <div className="faq-questions">
                  {section.questions.map((item, itemIndex) => {
                    const key = `${activeTab}-${sectionIndex}-${itemIndex}`;
                    const isOpen = openItems[key];
                    return (
                      <div key={itemIndex} className="faq-item">
                        <button
                          className={`faq-question ${isOpen ? 'open' : ''}`}
                          onClick={() => toggleItem(`${activeTab}-${sectionIndex}`, itemIndex)}
                        >
                          <div className="faq-question-content">
                            <RiQuestionLine className="faq-question-icon" />
                            <span>{item.q}</span>
                          </div>
                          <RiArrowDownSLine className={`faq-icon ${isOpen ? 'rotated' : ''}`} />
                        </button>
                        {isOpen && (
                          <div className="faq-answer">
                            <div className="faq-answer-content">
                              <RiInformationLine className="faq-answer-icon" />
                              <p>{item.a}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default Home;

